/**
 * Table Column Configurations
 *
 * Defines which columns to display for each table in the admin console,
 * along with their display properties (sortable, editable, type).
 */

export interface ColumnConfig {
  key: string;
  label: string;
  sortable: boolean;
  editable: boolean;
  type: 'text' | 'number' | 'date' | 'boolean' | 'json' | 'uuid';
  width?: string;
}

export const TABLE_COLUMNS: Record<string, ColumnConfig[]> = {
  users: [
    { key: 'cognito_id', label: 'Cognito ID', sortable: true, editable: false, type: 'uuid' },
    { key: 'email', label: 'Email', sortable: true, editable: true, type: 'text', width: '250px' },
    { key: 'display_name', label: 'Display Name', sortable: true, editable: true, type: 'text' },
    { key: 'created_at', label: 'Created', sortable: true, editable: false, type: 'date' },
  ],

  leagues: [
    { key: 'id', label: 'ID', sortable: true, editable: false, type: 'uuid', width: '100px' },
    { key: 'name', label: 'Name', sortable: true, editable: true, type: 'text', width: '250px' },
    { key: 'tournament_id', label: 'Tournament', sortable: true, editable: false, type: 'uuid' },
    { key: 'creator_id', label: 'Creator', sortable: true, editable: false, type: 'text' },
    { key: 'invite_code', label: 'Invite Code', sortable: false, editable: false, type: 'text' },
    { key: 'created_at', label: 'Created', sortable: true, editable: false, type: 'date' },
  ],

  league_members: [
    { key: 'id', label: 'ID', sortable: true, editable: false, type: 'uuid', width: '100px' },
    { key: 'league_id', label: 'League', sortable: true, editable: false, type: 'uuid' },
    { key: 'user_id', label: 'User', sortable: true, editable: false, type: 'text' },
    { key: 'team_name', label: 'Team Name', sortable: true, editable: true, type: 'text' },
    { key: 'total_points', label: 'Points', sortable: true, editable: true, type: 'number' },
    { key: 'status', label: 'Status', sortable: true, editable: true, type: 'text' },
    { key: 'joined_at', label: 'Joined', sortable: true, editable: false, type: 'date' },
  ],

  tournaments: [
    { key: 'id', label: 'ID', sortable: true, editable: false, type: 'uuid', width: '100px' },
    { key: 'name', label: 'Name', sortable: true, editable: true, type: 'text', width: '300px' },
    {
      key: 'wrestlestat_url',
      label: 'WrestleStat URL',
      sortable: false,
      editable: true,
      type: 'text',
    },
    {
      key: 'weight_classes',
      label: 'Weight Classes',
      sortable: false,
      editable: true,
      type: 'json',
    },
    {
      key: 'hashtags',
      label: 'Hashtags',
      sortable: false,
      editable: true,
      type: 'json',
    },
    { key: 'created_at', label: 'Created', sortable: true, editable: false, type: 'date' },
  ],

  wrestlers: [
    { key: 'id', label: 'ID', sortable: true, editable: false, type: 'uuid', width: '100px' },
    { key: 'tournament_id', label: 'Tournament', sortable: true, editable: false, type: 'uuid' },
    { key: 'name', label: 'Name', sortable: true, editable: true, type: 'text', width: '200px' },
    {
      key: 'school_team',
      label: 'Team',
      sortable: true,
      editable: true,
      type: 'text',
      width: '150px',
    },
    {
      key: 'weight_class',
      label: 'Weight',
      sortable: true,
      editable: true,
      type: 'text',
      width: '80px',
    },
    { key: 'seed', label: 'Seed', sortable: true, editable: true, type: 'number', width: '70px' },
    { key: 'place', label: 'Place', sortable: true, editable: true, type: 'number', width: '70px' },
    {
      key: 'team_points',
      label: 'Points',
      sortable: true,
      editable: true,
      type: 'number',
      width: '80px',
    },
  ],

  scraper_runs: [
    { key: 'id', label: 'ID', sortable: true, editable: false, type: 'uuid', width: '100px' },
    {
      key: 'tournament_name',
      label: 'Tournament',
      sortable: true,
      editable: false,
      type: 'text',
      width: '250px',
    },
    {
      key: 'scraper_type',
      label: 'Type',
      sortable: true,
      editable: false,
      type: 'text',
      width: '120px',
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      editable: false,
      type: 'text',
      width: '100px',
    },
    {
      key: 'started_at',
      label: 'Started',
      sortable: true,
      editable: false,
      type: 'date',
      width: '180px',
    },
    {
      key: 'duration_ms',
      label: 'Duration (ms)',
      sortable: true,
      editable: false,
      type: 'number',
      width: '120px',
    },
    {
      key: 'changes_count',
      label: 'Changes',
      sortable: true,
      editable: false,
      type: 'number',
      width: '100px',
    },
  ],

  team_rosters: [
    { key: 'id', label: 'ID', sortable: true, editable: false, type: 'uuid', width: '100px' },
    { key: 'league_member_id', label: 'Member', sortable: true, editable: false, type: 'uuid' },
    { key: 'wrestler_id', label: 'Wrestler', sortable: true, editable: false, type: 'uuid' },
    {
      key: 'weight_class',
      label: 'Weight',
      sortable: true,
      editable: false,
      type: 'text',
      width: '80px',
    },
    {
      key: 'wrestler_cost',
      label: 'Cost',
      sortable: true,
      editable: true,
      type: 'number',
      width: '80px',
    },
  ],

  league_messages: [
    { key: 'id', label: 'ID', sortable: true, editable: false, type: 'uuid', width: '100px' },
    { key: 'league_id', label: 'League', sortable: true, editable: false, type: 'uuid' },
    { key: 'user_id', label: 'User', sortable: true, editable: false, type: 'text' },
    {
      key: 'message',
      label: 'Message',
      sortable: false,
      editable: true,
      type: 'text',
      width: '400px',
    },
    { key: 'created_at', label: 'Created', sortable: true, editable: false, type: 'date' },
  ],
};

/**
 * Get default sort column for a table
 */
export function getDefaultSortColumn(table: string): string {
  // Most tables default to created_at, scraper_runs defaults to started_at
  if (table === 'scraper_runs') {
    return 'started_at';
  }
  if (table === 'league_members') {
    return 'joined_at';
  }
  return 'created_at';
}

/**
 * Get columns to display for a table
 */
export function getTableColumns(table: string): ColumnConfig[] {
  return TABLE_COLUMNS[table] || [];
}
